// What a symbol tends to mean, before it means anything about you.
//
// Held as a fixed lexicon rather than generated per-request for two reasons. It costs nothing and
// never varies, so the same symbol reads the same way every time you open it — an interpretation that
// quietly rewrites itself between visits is worse than none. And it keeps the general meaning clearly
// separate from Oreia's reading of *your* pattern, which is the part that is actually about you and
// is written from your own entries.
//
// Written in the app's register: what the image tends to carry, not a verdict. Each one is
// deliberately short — it is context for your own entries below it, not the answer.

const MEANINGS: Record<string, string> = {
  // ── Elements and landscape ──
  water: 'Feeling itself — whatever is moving under the surface. Its state tends to matter more than its presence: still, churning, rising, or frozen.',
  ocean: 'The size of something you cannot see the edge of. Often what is felt but not yet nameable.',
  sea: 'The size of something you cannot see the edge of. Often what is felt but not yet nameable.',
  river: 'Time and momentum — a direction already underway, whether or not it was chosen.',
  rain: 'Release, and sometimes relief. What was held is being let go of without being decided about.',
  storm: 'Pressure that has stopped being containable. Usually arrives after a long stretch of appearing fine.',
  fire: 'Wanting, anger, or transformation — the three are harder to tell apart than they seem.',
  earth: 'Ground, body, and what is actually solid. Often appears when something else has stopped being.',
  forest: 'Not knowing the way, in a place that is alive rather than empty. Being lost and being held at once.',
  mountain: 'What is being climbed, and the distance still in it.',
  desert: 'Depletion, or a stripping-back. What remains when the inessential is gone.',
  moon: 'What is felt in private. Cycles, intuition, the part of a life that is not on display.',
  sun: 'Attention, clarity, exposure. Being seen, wanted or not.',
  sky: 'Scale, and the reminder of it. Often relief, sometimes smallness.',
  snow: 'Stillness that is also suspension — something paused rather than finished.',
  ice: 'Feeling that has stopped moving. Preservation and paralysis at the same time.',
  meadow: 'Open ground with nothing demanded of you in it. Ease, and sometimes the exposure that comes with having no cover.',
  cave: 'Going inward, and what is kept where the light does not reach. Shelter and burial use the same room.',
  island: 'Separateness. Refuge or marooning, and the two are rarely distinguishable from inside.',

  // ── Structures and thresholds ──
  house: 'You — the whole of you. Rooms tend to correspond to parts of a life, and the state of them says the rest.',
  home: 'Belonging, and whether it is currently available to you.',
  room: 'A part of yourself you are in right now. Notice whether you knew it was there.',
  basement: 'What has been put below the level of daily attention. Not gone; stored.',
  attic: 'Memory and inheritance — what was kept, and what was kept out of sight.',
  door: 'A choice with a threshold in it. Whether it is open, locked, or unfamiliar is the substance.',
  window: 'Seeing without entering. Longing at a distance, or perspective held safely.',
  stairs: 'Effort and direction — descent as often as ascent, and rarely trivial either way.',
  bridge: 'A crossing between two states with nothing solid underneath. Transition made visible.',
  road: 'The path, and how much of it was chosen.',
  path: 'The path, and how much of it was chosen.',
  wall: 'A limit. Sometimes protection, sometimes the thing to be gotten past.',
  key: 'Access to something previously closed — including access you already hold and have not used.',
  mirror: 'Self-image, and the gap between how you see yourself and what is there.',
  church: 'Meaning, reverence, or the search for something larger. Not necessarily religious.',
  school: 'Being measured. Old formations of worth and performance.',
  hospital: 'Something that needs care and cannot be handled alone.',

  // ── Movement and states ──
  falling: 'Loss of control, or the letting go of it. Often surfaces around a real-life uncertainty.',
  flying: 'Freedom, escape, or perspective — the difference is whether you are going toward or away.',
  running: 'Urgency. What you are running toward, or from, is the question.',
  chased: 'Avoidance made literal. What pursues you is usually something declined rather than something foreign.',
  drowning: 'Overwhelm past the point of managing it.',
  lost: 'Direction gone, in a life or a decision. Often precedes rather than follows a change.',
  trapped: 'A situation with no visible exit — frequently one that is chosen and does not feel chosen.',
  hiding: 'Something not ready to be seen, including by you.',
  naked: 'Exposure and the fear of it. Being known without preparation.',
  teeth: 'Loss of power or composure. Classically tied to appearance, capability, and being able to speak.',
  blood: 'Life force, cost, and kinship — what something is taking out of you.',
  wound: 'An injury still doing work. Not the event; the part still open.',
  death: 'An ending, almost never a literal one. What is finishing so that something else can begin.',
  eye: 'Being seen, or finally seeing. Attention pointed outward and inward at once.',

  // ── Presences ──
  shadow: 'The part of yourself kept out of the picture. Not the bad part — the disowned part.',
  darkness: 'The unknown, before it has a shape. Fear and possibility are indistinguishable in it.',
  light: 'Awareness arriving. What becomes possible to look at.',
  child: 'Something young in you — either what was not finished, or what is only beginning.',
  mother: 'Care, origin, and its conditions. What nourished and what constrained.',
  father: 'Authority, structure, and your relationship to being judged.',
  stranger: 'A part of yourself not yet recognised as yours.',
  animal: 'Instinct, unedited. What you know before you reason about it.',
  snake: 'Transformation through shedding, and the fear that accompanies it. Healing and threat share this image.',
  bird: 'Perspective and release. What can leave.',
  dog: 'Loyalty, and what is owed in both directions.',
  cat: 'Autonomy, and something that cannot be compelled.',
  wolf: 'Instinct that is not tame, and does not want to be.',
  spider: 'Something being woven — patiently, and not always by you.',
  crowd: 'Being one of many. Belonging or erasure, depending on where you stand in it.',
  horse: 'Power you are carried by rather than power you hold. Whether it is ridden or loose is the substance.',
  bear: 'Strength that keeps its distance until it does not. Often protectiveness, sometimes what you are avoiding provoking.',
  butterfly: 'A change already completed. The version of you that could not have arrived without the interval before it.',
  fish: 'Something moving below the surface, glimpsed rather than held. What is known but not yet brought up.',
  egg: 'Before, rather than during. Something whole and unopened, and the waiting that belongs to it.',

  // ── Objects ──
  book: 'Something to be understood, or a story being told about you.',
  letter: 'A message not yet delivered — sometimes one you are the author of.',
  phone: 'Reaching for connection, and whether it goes through.',
  clock: 'Time pressure. Often about a deadline that is internal.',
  car: 'Your own agency — who is driving is rarely incidental.',
  train: 'A course already set, with limited say in it.',
  boat: 'Crossing feeling rather than land. What carries you through it.',
  garden: 'What is being tended, and what is growing without being asked.',
  tree: 'Continuity, roots, and long time. Growth measured in years.',
  flower: 'Something opening, and its brevity.',
  ring: 'Commitment, and the shape of a promise.',
  money: 'Worth and exchange — what you believe you are owed or must earn.',
  food: 'Nourishment, appetite, and what you allow yourself.',
  fabric: 'What covers, and what is being made.',
  arm: 'Reach, and what you are able to hold. Capability, or the asking that precedes it.',
  bags: 'What is being carried. Usually the question is how much of it is yours and whether it was ever set down.',
  store: 'Choosing among options, and what you believe is available to you. Abundance and overwhelm are the same aisle.',
  bed: 'Rest, intimacy, and privacy — the place you are least defended. Its state tends to say which of the three is at issue.',
  elevator: 'Movement you are not doing yourself. A change in level that is being decided elsewhere.',
  mask: 'The face assembled for other people. Useful, until it is the only one available.',
  music: 'Feeling in a form that bypasses explanation.',
};

/**
 * The general reading for a symbol, or null when there is no entry for it.
 *
 * Returning null matters: the detail view says nothing rather than reaching for a nearby word, so an
 * unusual symbol is left to your own entries instead of being explained by an approximation.
 */
export function symbolMeaning(symbol: string): string | null {
  const key = symbol.trim().toLowerCase();
  const bare = key.includes(':') ? key.slice(key.indexOf(':') + 1) : key;
  if (MEANINGS[bare]) return MEANINGS[bare];

  // "the ocean" / "dark water" should still find their head noun; a symbol is rarely a bare lemma
  // once it has been through extraction.
  const words = bare.split(/[\s-]+/).filter((w) => w.length > 2);
  for (const word of words) {
    const singular = word.endsWith('s') ? word.slice(0, -1) : word;
    if (MEANINGS[word]) return MEANINGS[word];
    if (MEANINGS[singular]) return MEANINGS[singular];
  }
  return null;
}
